import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AnalyticsPanel = ({ analytics, isExpanded, onToggle }) => {
  const [selectedMetric, setSelectedMetric] = useState('overview');

  const metrics = [
    {
      id: 'totalJourneys',
      label: 'Total Journeys',
      value: analytics?.totalJourneys,
      icon: 'BookOpen',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      id: 'activeStudents',
      label: 'Active Students',
      value: analytics?.activeStudents,
      icon: 'Users',
      color: 'text-accent',
      bgColor: 'bg-accent/10',
    },
    {
      id: 'completionRate',
      label: 'Avg Completion',
      value: `${analytics?.avgCompletionRate}%`,
      icon: 'TrendingUp',
      color: 'text-success',
      bgColor: 'bg-success/10',
    },
    {
      id: 'pendingQuestions',
      label: 'Pending Questions',
      value: analytics?.pendingQuestions,
      icon: 'MessageCircle',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
    },
  ];

  return (
    <div className="bg-card rounded-lg shadow-sm overflow-hidden">
      <div className="p-4 md:p-5 lg:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="BarChart3" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-semibold text-foreground">Analytics Overview</h2>
              <p className="text-sm text-muted-foreground">Real-time performance metrics</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            onClick={onToggle}
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </div>
      </div>
      <div className="p-4 md:p-5 lg:p-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {metrics?.map((metric) => (
            <div
              key={metric?.id}
              className="bg-background rounded-lg p-4 hover-lift transition-organic cursor-pointer"
              onClick={() => setSelectedMetric(metric?.id)}
            >
              <div className={`w-12 h-12 ${metric?.bgColor} rounded-lg flex items-center justify-center mb-3`}>
                <Icon name={metric?.icon} size={24} className={metric?.color} />
              </div>
              <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">{metric?.value}</p>
              <p className="text-xs md:text-sm text-muted-foreground">{metric?.label}</p>
            </div>
          ))}
        </div>

        {isExpanded && (
          <div className="space-y-6 stagger-fade-in">
            <div className="bg-background rounded-lg p-4 md:p-5">
              <h3 className="text-base md:text-lg font-semibold text-foreground mb-4 flex items-center">
                <Icon name="Activity" size={20} className="mr-2 text-primary" />
                Recent Activity
              </h3>
              <div className="space-y-3">
                {analytics?.recentActivity?.map((activity) => (
                  <div key={activity?.id} className="flex items-start space-x-3 pb-3 border-b border-border last:border-0">
                    <div className={`w-8 h-8 ${activity?.bgColor} rounded-full flex items-center justify-center flex-shrink-0`}>
                      <Icon name={activity?.icon} size={16} className={activity?.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground">{activity?.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">{activity?.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-background rounded-lg p-4 md:p-5">
              <h3 className="text-base md:text-lg font-semibold text-foreground mb-4 flex items-center">
                <Icon name="Award" size={20} className="mr-2 text-primary" />
                Top Performing Trails
              </h3>
              <div className="space-y-3">
                {analytics?.topJourneys?.map((journey, index) => (
                  <div key={journey?.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <span className="text-lg font-bold text-muted-foreground w-6">#{index + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{journey?.title}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-success">{journey?.completionRate}%</p>
                      <p className="text-xs text-muted-foreground">{journey?.students} students</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPanel;